<template>
  <div
    class="popup-content-user"
    v-bind:class="{ 'icon-only-container': showIconsOnly }"
  >
    <div v-if="showIconsOnly" class="icon-only">
      <div
        v-for="datum in dataForIcons"
        v-bind:key="datum.id"
        class="icon-wrapper"
      >
        <b-skeleton
          v-show="!datum.loaded"
          type="avatar"
          width="24px"
          height="24px"
        />
        <b-card-img
          v-show="datum.loaded"
          v-bind:src="datum.url"
          v-on:load="datum.loaded = true"
          v-on:error="
            (event) => {
              event.target.src = defaultIcon
              datum.loaded = true
            }
          "
          v-on:click="$emit('detail-click', datum.id)"
          class="profile-icon"
          crossorigin="use-credentials"
        />
      </div>
    </div>
    <card-user-small
      v-else
      v-for="datum in dataForCards"
      v-bind:key="datum.id"
      v-bind="datum"
      class="content-item"
      v-on="$listeners"
    />
  </div>
</template>

<script>
import { RestApiClient } from 'ifield-core-components'
import CardUserSmall from '@/components/AppCardUserSmall.vue'
import { mapState } from 'vuex'
import defaultIcon from '@/assets/ifield/user-image-default-gray@3x.png'
import {
  getItemDotColor,
  getItemIcon,
  getItemText,
} from '@/js/util/UserLabelItemText.js'
import { EMPTY_ITEM_TEXT, findLabelItem } from '@/js/util/UserLabelUtil.js'

export default {
  name: 'AppMapPopupContentUser',
  components: {
    'card-user-small': CardUserSmall,
  },
  props: {
    /**
     * ユーザーデータ
     */
    data: {
      type: Array,
      default: () => [],
    },
    /**
     * ソート関数
     */
    sortFunction: {
      type: Function,
      default: null,
    },
    /**
     * 2段目に出す表示項目（表示中の並びの先頭）
     */
    dispItem: {
      type: String,
      default: null,
    },
    /**
     * 相対時間を計算する際の基準日時
     */
    baseDate: {
      type: Date,
      default: null,
    },
  },
  data: function () {
    return {
      defaultIcon,
      dataForIcons: [],
    }
  },
  mounted: function () {
    if (this.showIconsOnly) {
      try {
        this.dataForIcons = this.data.map((datum) => {
          const url = RestApiClient.createApiUrl('download_file')
          const params = url.searchParams
          params.append('id', datum.id)
          params.append('thumbnail', 1)
          params.append('attach_file_type', 9)
          return { id: datum.id, url: url.toString(), loaded: false }
        })
      } catch (error) {
        this.$log.error('Failed to load profile icons:', error)
      }
    }
  },
  computed: {
    ...mapState({
      /**
       * アイコンのみ表示設定 (true: アイコン, false: リスト)
       */
      showIconsOnly: (state) => state.showIconsOnly,
    }),
    /**
     * カード用データ
     * @type {Array.<Object>}
     */
    dataForCards: function () {
      if (!this.data) return []

      let data = [...this.data]

      this.sortFunction && data.sort(this.sortFunction)

      return data.map((datum) => {
        // 2段目はラベルと同じ内容を出す（仕様: cluster.png）
        const item = findLabelItem(datum, this.dispItem)
        const itemText = item ? getItemText(item, this.baseDate) : ''
        return {
          id: datum.id,
          name: datum.user_name,
          // ラベルと揃えて、値が無くても2段目は出す（2026-08-20 決定）
          itemText: this.dispItem ? itemText || EMPTY_ITEM_TEXT : '',
          itemIcon: getItemIcon(item),
          itemDotColor: getItemDotColor(item),
        }
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.popup-content-user {
  // 2段目がある場合は1行が高くなるため、高さは行の高さから組み立てる
  --item-height: 3.5rem;

  max-width: 15rem;
  // アイテムの高さ * 最大表示数 - 余白調整分
  max-height: calc(var(--item-height) * 4.5 - 4px);
  overflow-x: hidden;
  overflow-y: auto;
  @include mixin-scrollbar;

  &.icon-only-container {
    // iconOnly: アイコン4.5行分 + gap3.5行分
    max-height: calc(24px * 4.5 + 8px * 3.5);
    .icon-only {
      display: grid;
      grid-template-columns: repeat(5, 1fr);
      gap: 8px;
      justify-items: center;
      align-items: center;
      // b-skeletonとb-card-imgの表示切り替え時のリフロー対策
      .icon-wrapper {
        width: 24px;
        height: 24px;
      }
      .profile-icon {
        width: 24px;
        height: 24px;
        border-radius: 50%;
        cursor: pointer;
      }
    }
  }
}

.content-item {
  &:first-child {
    padding-top: 0;
  }

  &:last-child {
    padding-bottom: 0;
    border-bottom: 1px solid transparent;
  }

  &:not(:last-child) {
    border-bottom: 1px solid #e3e3e3;
  }
}
</style>
